import { OptionTemplate } from "shared/api/middleware-api-client";
import {
  isConditionalUNewLayoutCode,
  isDubbelConditionalUNewLayoutCode,
  isLockHandleLayoutCode,
  isUHandleLengthWarningLayoutCode,
} from "entities/configurator/model/layoutMeta";

export type HandleFamilyPolicy =
  | "unrestricted"
  | "rounded-layout"
  | "conditional-u-new-layout"
  | "vertical-only";

export type HandleContext = {
  anchorBeamGap?: number | null;
  layoutCode?: string | null;
};

export type RestrictedHandleFamilyPolicy = Exclude<
  HandleFamilyPolicy,
  "unrestricted"
>;

export type HandleModeConfig = {
  preferredCodes?: readonly string[];
  isAllowed?: (
    handleCode: string | null | undefined,
    context: HandleContext,
  ) => boolean;
  shouldKeepOnTransition?: (
    handleCode: string | null | undefined,
    context: HandleContext,
  ) => boolean;
  findFallback?: (
    handleOptions: OptionTemplate[] | undefined,
    context: HandleContext,
  ) => OptionTemplate | null;
};

export type DoorHandleProfile = Partial<
  Record<RestrictedHandleFamilyPolicy, HandleModeConfig>
> & {
  roundedExitRestoreCodes?: readonly string[];
  conditionalULayoutMatcher?: (code: string | null | undefined) => boolean;
};

export const maxUHandleBeamGap = 2100;

export const uHandleCodes: readonly string[] = [
  "verticalhandle",
  "verticalhandlehout",
  "verticalhandleopdekdeurkozijn",
  "verticalhandletaatsschuif",
  "verticalhandletaatsschuifdubbel",
];

export const genericInvalidHandleCodes: readonly string[] = ["normalhandle"];

export const hiddenHandleCodesForNewLayouts: readonly string[] = [
  "verticalhandletaatsschuifdubbel",
  "scharnierdeurkruk",
  "scharniertoiletbeslag",
  "scharniercilinderslot",
];

export const hiddenHandleCodesForClassicLayouts: readonly string[] = [
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
  "normalhandlescharnierronddubbel",
];

export const roundedHandleCodes: readonly string[] = [
  "normalhandlescharnierronddubbel",
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
  "normalhandleschuif",
];

export const roundedLayoutExitHandleCodes: readonly string[] = [
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
  "normalhandlescharnierronddubbel",
];

const classicNonRoundedHandleCodes: readonly string[] = [
  "normalhandletaats",
  "normalhandletaatsdubbel",
];

const taatsConditionalUPreferredHandleCodes: readonly string[] = [
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
  "normalhandletaats",
  "normalhandletaatsdubbel",
];

const taatsConditionalUTransitionKeepHandleCodes = new Set([
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
]);

const dubbelConditionalUAllowedHandleCodes = new Set([
  "normalhandletaatsdubbel",
  "verticalhandletaatsschuifdubbel",
  "normalhandletaats",
  "normalhandletaatsronddubbel",
]);

const dubbelConditionalUPreferredHandleCodes: readonly string[] = [
  "normalhandletaatsronddubbel",
  "normalhandletaatsdubbel",
  "normalhandletaats",
];

const schuifConditionalUPreferredHandleCodes: readonly string[] = [
  "normalhandleschuif",
];

const dubbeleschuifConditionalUHandleCodes = new Set([
  "verticalhandle",
  "verticalhandletaatsschuif",
  "verticalhandletaatsschuifdubbel",
]);

const scharnierConditionalUPreferredHandleCodes = new Set([
  "normalhandletaatsrond",
  "normalhandletaats",
]);

const scharnierRoundedPreferredHandleCodes: readonly string[] = [
  "normalhandlescharnierronddubbel",
  "normalhandletaatsrond",
  "normalhandletaatsronddubbel",
  "normalhandleschuif",
];

const dubbelescharnierConditionalUPreferredHandleCodes: readonly string[] = [
  "normalhandlescharnierronddubbel",
];

const scharnierLockHandleCodes = new Set([
  "scharnierdeurkruk",
  "scharniertoiletbeslag",
  "scharniercilinderslot",
]);

const opdekLockHandleCodes = new Set([
  "normalhandle",
  "toiletbeslag",
  "cilinderslot",
]);

const opdekFamilyDoorCodes = new Set(["opdekdeur", "opdekdeurmetkozijn"]);

const conditionalUDoubleVisibilityDoorCodes = new Set([
  "dubbel",
  "dubbeleschuifdeur",
  "dubbelescharnierdeur",
]);

export const isVerticalOnlyDoorCode = (
  doorCode: string | null | undefined,
): boolean =>
  !!doorCode &&
  (opdekFamilyDoorCodes.has(doorCode) || doorCode === "stompedeur");

export const shouldKeepConditionalDoubleHandleVisible = (
  handleCode: string | null | undefined,
  doorCode: string | null | undefined,
  layoutCode: string | null | undefined,
): boolean =>
  handleCode === "verticalhandletaatsschuifdubbel" &&
  !!doorCode &&
  conditionalUDoubleVisibilityDoorCodes.has(doorCode) &&
  isDubbelConditionalUNewLayoutCode(layoutCode);

export const isUHandleCode = (code: string | null | undefined): boolean =>
  !!code && uHandleCodes.includes(code);

export const exceedsMaxUHandleBeamGap = (
  anchorBeamGap: number | null | undefined,
): boolean =>
  anchorBeamGap !== null &&
  anchorBeamGap !== undefined &&
  anchorBeamGap > maxUHandleBeamGap;

export const isConditionalUHandleAllowed = (
  anchorBeamGap: number | null | undefined,
  layoutCode?: string | null,
): boolean =>
  isUHandleLengthWarningLayoutCode(layoutCode) ||
  (anchorBeamGap !== null &&
    anchorBeamGap !== undefined &&
    anchorBeamGap <= maxUHandleBeamGap);

const isScharnierLockHandleCode = (code: string | null | undefined): boolean =>
  !!code && scharnierLockHandleCodes.has(code);

const isOpdekLockHandleCode = (code: string | null | undefined): boolean =>
  !!code && opdekLockHandleCodes.has(code);

/**
 * Lock handles (kruk, toiletbeslag, cilinderslot) are only offered on the
 * layouts flagged with `lockHandles` (de Kaap / de Cool De Waal).
 */
export const isLockHandleAllowedOnLayout = (
  handleCode: string | null | undefined,
  layoutCode: string | null | undefined,
): boolean =>
  (isScharnierLockHandleCode(handleCode) ||
    isOpdekLockHandleCode(handleCode)) &&
  isLockHandleLayoutCode(layoutCode);

const isTaatsConditionalUFallbackHandleCode = (
  code: string | null | undefined,
): boolean => !!code && !isUHandleCode(code) && code !== "normalhandleschuif";

const isScharnierConditionalUHandleAllowed = (
  handleCode: string | null | undefined,
  { anchorBeamGap, layoutCode }: HandleContext,
): boolean =>
  !!handleCode &&
  (scharnierConditionalUPreferredHandleCodes.has(handleCode) ||
    isLockHandleAllowedOnLayout(handleCode, layoutCode) ||
    (isUHandleCode(handleCode) &&
      isConditionalUHandleAllowed(anchorBeamGap, layoutCode)));

const isDubbelConditionalUHandleAllowed = (
  handleCode: string | null | undefined,
  { anchorBeamGap, layoutCode }: HandleContext,
): boolean =>
  !!handleCode &&
  dubbelConditionalUAllowedHandleCodes.has(handleCode) &&
  (!isUHandleCode(handleCode) ||
    isConditionalUHandleAllowed(anchorBeamGap, layoutCode));

const isDubbeleschuifConditionalUHandleAllowed = (
  handleCode: string | null | undefined,
  { anchorBeamGap, layoutCode }: HandleContext,
): boolean =>
  !!handleCode &&
  (!isUHandleCode(handleCode) ||
    (dubbeleschuifConditionalUHandleCodes.has(handleCode) &&
      isConditionalUHandleAllowed(anchorBeamGap, layoutCode)));

const isDubbelescharnierConditionalUHandleAllowed = (
  handleCode: string | null | undefined,
  { anchorBeamGap, layoutCode }: HandleContext,
): boolean =>
  !!handleCode &&
  (!isScharnierLockHandleCode(handleCode) ||
    isLockHandleAllowedOnLayout(handleCode, layoutCode)) &&
  (!isUHandleCode(handleCode) ||
    isConditionalUHandleAllowed(anchorBeamGap, layoutCode));

export const findFirstMatchingHandle = (
  handleOptions: OptionTemplate[] | undefined,
  predicate: (handle: OptionTemplate) => boolean,
): OptionTemplate | null => (handleOptions ?? []).find(predicate) ?? null;

export const findFirstHandleByCodes = (
  handleOptions: OptionTemplate[] | undefined,
  preferredCodes: readonly string[],
): OptionTemplate | null => {
  const usableHandles = handleOptions ?? [];

  for (const code of preferredCodes) {
    const match = usableHandles.find((handle) => handle.code === code);
    if (match) {
      return match;
    }
  }

  return null;
};

export const findFirstNonUHandle = (
  handleOptions: OptionTemplate[] | undefined,
): OptionTemplate | null =>
  findFirstMatchingHandle(
    handleOptions,
    (handle) => !isUHandleCode(handle.code),
  );

const findFirstTaatsConditionalUFallbackHandle = (
  handleOptions: OptionTemplate[] | undefined,
): OptionTemplate | null =>
  findFirstMatchingHandle(handleOptions, (handle) =>
    isTaatsConditionalUFallbackHandleCode(handle.code),
  );

const findFirstScharnierConditionalUFallbackHandle = (
  handleOptions: OptionTemplate[] | undefined,
): OptionTemplate | null => findFirstHandleByCodes(handleOptions, uHandleCodes);

const findFirstDubbeleschuifConditionalUFallbackHandle = (
  handleOptions: OptionTemplate[] | undefined,
  context: HandleContext,
): OptionTemplate | null => {
  const firstNonUHandle = findFirstNonUHandle(handleOptions);
  if (firstNonUHandle) {
    return firstNonUHandle;
  }

  return findFirstMatchingHandle(handleOptions, (handle) =>
    isDubbeleschuifConditionalUHandleAllowed(handle.code, context),
  );
};

const findFirstDubbelescharnierConditionalUFallbackHandle = (
  handleOptions: OptionTemplate[] | undefined,
  context: HandleContext,
): OptionTemplate | null => {
  const firstNonUHandle = findFirstMatchingHandle(
    handleOptions,
    (handle) =>
      !isUHandleCode(handle.code) && !isScharnierLockHandleCode(handle.code),
  );
  if (firstNonUHandle) {
    return firstNonUHandle;
  }

  return findFirstMatchingHandle(handleOptions, (handle) =>
    isDubbelescharnierConditionalUHandleAllowed(handle.code, context),
  );
};

const roundedDefaultModeConfig: HandleModeConfig = {
  preferredCodes: roundedHandleCodes,
};

const roundedScharnierModeConfig: HandleModeConfig = {
  preferredCodes: scharnierRoundedPreferredHandleCodes,
};

const roundedRestrictedScharnierModeConfig: HandleModeConfig = {
  preferredCodes: scharnierRoundedPreferredHandleCodes,
  isAllowed: (handleCode) =>
    !!handleCode &&
    !isUHandleCode(handleCode) &&
    !isScharnierLockHandleCode(handleCode),
};

const taatsConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: taatsConditionalUPreferredHandleCodes,
  shouldKeepOnTransition: (handleCode) =>
    !!handleCode && taatsConditionalUTransitionKeepHandleCodes.has(handleCode),
  findFallback: (handleOptions) =>
    findFirstTaatsConditionalUFallbackHandle(handleOptions),
};

const schuifConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: schuifConditionalUPreferredHandleCodes,
  shouldKeepOnTransition: (handleCode) =>
    !!handleCode && schuifConditionalUPreferredHandleCodes.includes(handleCode),
};

const scharnierConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: Array.from(scharnierConditionalUPreferredHandleCodes),
  isAllowed: (handleCode, context) =>
    isScharnierConditionalUHandleAllowed(handleCode, context),
  shouldKeepOnTransition: (handleCode, { layoutCode }) =>
    !!handleCode &&
    (scharnierConditionalUPreferredHandleCodes.has(handleCode) ||
      isUHandleCode(handleCode) ||
      isLockHandleAllowedOnLayout(handleCode, layoutCode)),
  findFallback: (handleOptions) =>
    findFirstScharnierConditionalUFallbackHandle(handleOptions),
};

const dubbelConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: dubbelConditionalUPreferredHandleCodes,
  isAllowed: (handleCode, context) =>
    isDubbelConditionalUHandleAllowed(handleCode, context),
  shouldKeepOnTransition: (handleCode) =>
    !!handleCode && dubbelConditionalUAllowedHandleCodes.has(handleCode),
  findFallback: (handleOptions) =>
    findFirstHandleByCodes(
      handleOptions,
      dubbelConditionalUPreferredHandleCodes,
    ),
};

const dubbeleschuifRoundedModeConfig: HandleModeConfig = {
  preferredCodes: schuifConditionalUPreferredHandleCodes,
};

const dubbeleschuifConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: schuifConditionalUPreferredHandleCodes,
  isAllowed: (handleCode, context) =>
    isDubbeleschuifConditionalUHandleAllowed(handleCode, context),
  shouldKeepOnTransition: (handleCode) =>
    !!handleCode &&
    (schuifConditionalUPreferredHandleCodes.includes(handleCode) ||
      dubbeleschuifConditionalUHandleCodes.has(handleCode)),
  findFallback: (handleOptions, context) =>
    findFirstDubbeleschuifConditionalUFallbackHandle(handleOptions, context),
};

const dubbelescharnierConditionalUModeConfig: HandleModeConfig = {
  preferredCodes: dubbelescharnierConditionalUPreferredHandleCodes,
  isAllowed: (handleCode, context) =>
    isDubbelescharnierConditionalUHandleAllowed(handleCode, context),
  shouldKeepOnTransition: (handleCode, { layoutCode }) =>
    !!handleCode &&
    (!isScharnierLockHandleCode(handleCode) ||
      isLockHandleAllowedOnLayout(handleCode, layoutCode)),
  findFallback: (handleOptions, context) =>
    findFirstDubbelescharnierConditionalUFallbackHandle(handleOptions, context),
};

const verticalOnlyModeConfig: HandleModeConfig = {
  preferredCodes: uHandleCodes,
  isAllowed: (handleCode, { layoutCode }) =>
    isUHandleCode(handleCode) ||
    isLockHandleAllowedOnLayout(handleCode, layoutCode),
};

export const doorHandleProfiles: Partial<Record<string, DoorHandleProfile>> = {
  taatsdeur: {
    "rounded-layout": roundedDefaultModeConfig,
    "conditional-u-new-layout": taatsConditionalUModeConfig,
    conditionalULayoutMatcher: isConditionalUNewLayoutCode,
  },
  schuifdeur: {
    "rounded-layout": roundedDefaultModeConfig,
    "conditional-u-new-layout": schuifConditionalUModeConfig,
    conditionalULayoutMatcher: isConditionalUNewLayoutCode,
  },
  scharnierdeur: {
    "rounded-layout": roundedScharnierModeConfig,
    "conditional-u-new-layout": scharnierConditionalUModeConfig,
    conditionalULayoutMatcher: isConditionalUNewLayoutCode,
  },
  dubbel: {
    "rounded-layout": roundedDefaultModeConfig,
    "conditional-u-new-layout": dubbelConditionalUModeConfig,
    conditionalULayoutMatcher: isDubbelConditionalUNewLayoutCode,
    roundedExitRestoreCodes: ["normalhandletaatsdubbel", "normalhandletaats"],
  },
  dubbeleschuifdeur: {
    "rounded-layout": dubbeleschuifRoundedModeConfig,
    "conditional-u-new-layout": dubbeleschuifConditionalUModeConfig,
    conditionalULayoutMatcher: isDubbelConditionalUNewLayoutCode,
  },
  dubbelescharnierdeur: {
    "rounded-layout": roundedRestrictedScharnierModeConfig,
    "conditional-u-new-layout": dubbelescharnierConditionalUModeConfig,
    conditionalULayoutMatcher: isDubbelConditionalUNewLayoutCode,
  },
  schuifdeur1paneel: {
    "rounded-layout": roundedDefaultModeConfig,
  },
  taatsdeur1paneel: {
    "rounded-layout": roundedDefaultModeConfig,
  },
  scharnierdeur1paneel: {
    "rounded-layout": roundedScharnierModeConfig,
  },
  dubbel2paneel: {
    "rounded-layout": roundedDefaultModeConfig,
  },
  dubbeleschuifdeur2paneel: {
    "rounded-layout": roundedDefaultModeConfig,
  },
  dubbelescharnierdeur2paneel: {
    "rounded-layout": roundedScharnierModeConfig,
  },
  opdekdeur: {
    "vertical-only": verticalOnlyModeConfig,
  },
  opdekdeurmetkozijn: {
    "vertical-only": verticalOnlyModeConfig,
  },
  stompedeur: {
    "vertical-only": verticalOnlyModeConfig,
  },
};

export const defaultRoundedExitRestoreCodes: readonly string[] =
  classicNonRoundedHandleCodes;
